// pi-live.ts — the DOM-free fold of pi's 0.84 RPC event stream into a render
// model. Unit-testable; the PiMessageView (pi-view.ts) renders it.
//
// ⚠ 0.84 SEMANTICS, NOT the pre-0.84 pcc fold: `message_update` carries ONLY
// an `assistantMessageEvent` delta keyed by `contentIndex` — there is no
// cumulative `message` snapshot on updates. The in-flight message is
// ASSEMBLED here from message_start + deltas; `message_end.message` is
// authoritative and replaces the assembly. (Ranked risk #1 in the plan —
// fixtures for this fold are recorded from a real 0.84.1 session.)

export interface PiToolCall {
  id: string;
  name: string;
  arguments?: unknown;
}

export interface PiBlock {
  type: 'text' | 'thinking' | 'toolCall' | 'attachment' | string;
  text?: string;
  thinking?: string;
  /** toolCall blocks: present once toolcall_end delivered the full call;
   * while streaming, argsRaw accumulates the raw JSON string deltas. */
  toolCall?: PiToolCall;
  argsRaw?: string;
  streaming?: boolean;
  /** thinking blocks — measured client-side (thinking_start → thinking_end
   * wall clock; pi stamps no per-block timing) and carried onto the
   * authoritative message at message_end, so the settled step can read
   * "Thought for 2 min 14 sec". Absent on snapshot-hydrated transcripts the
   * view never watched live. */
  thinkingStartedAt?: number;
  thinkingMs?: number;
}

/** What the SERVER emits in place of a raw image block or an attachment
 * manifest entry (see server/pi/attachments.ts). The base64 never reaches the
 * browser and the manifest is already stripped — a `pi-snapshot` re-sends the
 * whole transcript on every attach, so shipping either would re-transfer
 * megabytes on every tab switch. */
export interface PiAttachmentBlock {
  type: 'attachment';
  label: string;
  name: string;
  kind: 'image' | 'file' | 'folder';
  mime: string;
  /** '' when the file is no longer servable (a resumed session's foreign
   * path) — the chip still renders, it just isn't clickable. Folders are
   * always '' (a staged directory has no single fetchable url). */
  url: string;
}

export interface PiMessage {
  role: string; // user | assistant | toolResult | bashExecution | ...
  content?: unknown; // string or block array, per role
  toolCallId?: string;
  toolName?: string;
  isError?: boolean;
  timestamp?: number;
  usage?: Record<string, unknown>;
  /** Who sent a user message. Durable-transcript rows carry it from the
   * store (the console RM lane did, until it retired in P16); wire-envelope echoes and injected-message markers gain it
   * at ingest here (normalizeUserEnvelope). `userId: 'local'` is the synthetic
   * solo identity and renders chipless; null = a display name with no account
   * behind it. */
  sender?: PiSenderRef;
  /** Reply-to reference (owner item 54): the transcript ordinal + quoted span
   * this message replies to. Lifted from the trailing `<helm-reply-to>` fence
   * at ingest (server rewriteMessage on the Bridge lane; normalizeUserEnvelope
   * here for the raw-wire lanes) — the pill renders from THIS field, never by
   * re-parsing text. */
  replyTo?: { ord: number; text: string };
  /** Parsed `<coordination …>` envelope attrs (event, scope, items, seq, and
   * the optional esc_* card seats), attached at ingest by
   * normalizeUserEnvelope for layer-injected rows ONLY. The envelope markup
   * itself is stripped from `content` (the cause text is what renders), so
   * this field is the one carrier of the structured event — pi-steps reads
   * it to promote escalation lifecycle events into first-class cards.
   * Absent on every other message; unknown attrs ride along untouched. */
  coord?: Record<string, string>;
  [k: string]: unknown;
}

/** A message's sender, as every operator-facing chat surface renders it. */
export interface PiSenderRef {
  userId: string | null;
  name: string;
  /** What KIND of actor sent it — drives the avatar lane: 'user' (default)
   * renders the photo-or-initials node; 'agent' / 'coordination' / 'system'
   * render the Channels identicon so one sender has one face everywhere. */
  kind?: 'user' | 'agent' | 'coordination' | 'system';
  /** Identity key for the identicon lane, in the Channels key grammar
   * (channels.ts avKeyFor): scope for cross-scope senders, 'system' for the
   * coordination layer. Absent = the host derives it (session scope § name). */
  key?: string;
  /** OIDC profile picture (console users). Dead URLs fall back to initials. */
  pictureUrl?: string | null;
  /** An agent-to-agent sender's relationship to the session it is rendered
   * in, as the harness recorded it (prime-agent `agent_message`
   * details.fromRelationship; 'peer' when it named none). The chip shows it
   * as a role tag beside the name. Absent on every other sender. */
  role?: AgentMessageRole;
}

export interface PiToolRun {
  toolCallId: string;
  toolName: string;
  args?: unknown;
  /** Accumulated partial result (update events REPLACE — pi sends the
   * accumulated content, not a delta). */
  partial?: unknown;
  done: boolean;
  isError?: boolean;
}

interface Inflight {
  role: string;
  blocks: Map<number, PiBlock>;
  order: number[];
}

export type PiChange = 'messages' | 'inflight' | 'tools' | 'state' | 'none';

/** The working-indicator gerund pool (ported verbatim from the corpus
 * viewer's chat — "Claude Code parity" working line). Cycled every ~4s from
 * a per-turn seed so the verb is stable within a window and varies per turn. */
export const SPINNER_VERBS = [
  'Improvising',
  'Reticulating',
  'Spelunking',
  'Cross-referencing',
  'Deliberating',
  'Mulling',
  'Truffle-hunting',
  'Excavating',
  'Triangulating',
  'Sleuthing',
  'Perusing',
  'Rummaging',
  'Unearthing',
  'Synthesizing',
  'Dowsing',
  'Cogitating',
];

export class PiLiveModel {
  messages: PiMessage[] = [];
  inflight: Inflight | null = null;
  toolRuns = new Map<string, PiToolRun>();
  turnOpen = false;
  compacting = false;
  /** Pending steering/follow-up queue (queue_update). */
  queuedSteering: string[] = [];
  queuedFollowUp: string[] = [];
  /** Per-turn stats feeding the working line (reset at agent_start). */
  turnTokens = 0;
  turnToolsDone = 0;
  /** Verb-pool offset, advanced per turn so consecutive turns don't open on
   * the same gerund. */
  verbSeed = 0;

  loadSnapshot(
    messages: unknown[],
    state?: {
      turnOpen?: boolean;
      turnTokens?: number;
      turnToolsDone?: number;
      queuedSteering?: string[];
      queuedFollowUp?: string[];
    },
  ): void {
    // Snapshots are normally clean (the Bridge strips fences server-side), but
    // a legacy source — the retired (P16) console RM lane's S3 backfill of pi's
    // own session.jsonl — replayed COMPOSED prompts, and a stored transcript
    // can still carry them.
    // Same parser as the live lane, so reload and live-append cannot disagree.
    //
    this.messages = (((messages as PiMessage[]) ?? [])).flatMap((m) => normalizeUserEnvelope(m));
    this.inflight = null;
    this.toolRuns.clear();
    if (state?.turnOpen !== undefined) this.turnOpen = state.turnOpen;
    // Per-run counters come from the server's fold — a fresh view's own fold
    // saw none of the run, so re-attaching must not restart them at zero.
    this.turnTokens = typeof state?.turnTokens === 'number' ? state.turnTokens : 0;
    this.turnToolsDone = typeof state?.turnToolsDone === 'number' ? state.turnToolsDone : 0;
    // Item 36: pi's own steering/follow-up queue rides the snapshot (the
    // driver caches the last queue_update) — a viewer attaching between a
    // steer and its turn-boundary injection still sees the queued bubble.
    if (state?.queuedSteering) this.queuedSteering = state.queuedSteering;
    if (state?.queuedFollowUp) this.queuedFollowUp = state.queuedFollowUp;
  }

  /**
   * Prepend an OLDER page of history to the front of the transcript (lazy-
   * load-up — transcripts reach all the way back). Parsed through the SAME
   * envelope normalizer as loadSnapshot/live-append, so a paged-in message
   * renders byte-identically. Deduped at the seam by message id: the page is
   * `[start, before)` and the model's front is `before`, so they are disjoint
   * by construction, but a defensive id check keeps a re-fetch idempotent.
   */
  prependMessages(older: unknown[]): void {
    const normalized = ((older as PiMessage[]) ?? []).flatMap((m) => normalizeUserEnvelope(m));
    if (!normalized.length) return;
    const haveIds = new Set<string>();
    for (const m of this.messages) {
      const id = (m as { id?: unknown }).id;
      if (typeof id === 'string' && id) haveIds.add(id);
    }
    const fresh = normalized.filter((m) => {
      const id = (m as { id?: unknown }).id;
      return !(typeof id === 'string' && id && haveIds.has(id));
    });
    if (!fresh.length) return;
    this.messages = [...fresh, ...this.messages];
  }

  /** True when a user echo's normalized sections merely re-deliver the messages
   * already at the tail — the snapshot∩live-buffer overlap (see the message_end
   * user path). clientMsgId, when BOTH copies carry one (the retired console
   * RM lane did), is authoritative: distinct ids ⇒ distinct messages, kept. The id-less BRIDGE
   * lane falls back to sender-name + trimmed text. Conservative: an id on only
   * one side proves nothing, so the echo is kept. */
  duplicatesTail(incoming: PiMessage[]): boolean {
    const n = incoming.length;
    if (n === 0) return true; // an empty echo adds nothing
    if (this.messages.length < n) return false;
    const base = this.messages.length - n;
    for (let i = 0; i < n; i++) {
      const a = this.messages[base + i]!;
      const b = incoming[i]!;
      if (a.role !== 'user' || b.role !== 'user') return false;
      const aId = (a as { clientMsgId?: unknown }).clientMsgId;
      const bId = (b as { clientMsgId?: unknown }).clientMsgId;
      if (typeof aId === 'string' && aId && typeof bId === 'string' && bId) {
        if (aId !== bId) return false;
      } else if ((typeof aId === 'string' && aId) || (typeof bId === 'string' && bId)) {
        return false; // one side carries an id, the other doesn't — cannot prove same
      }
      if (contentText(a.content).trim() !== contentText(b.content).trim()) return false;
      if ((a.sender?.name ?? '') !== (b.sender?.name ?? '')) return false;
    }
    return true;
  }

  /** P22 lane u4 — the Helm server's PERSISTED host cards (`pi-host-cards`,
   * after a snapshot): each lands after the last message whose transcript
   * ordinal is below its `afterOrd` (and after any card already placed
   * there), so it rejoins the turn it followed, in turn order. A card from
   * before the loaded window is skipped (it belongs to an older page); with
   * nothing stamped at all it is appended. Returns how many were placed.
   * Inlined validation: the smoke transpiles this file standalone. */
  placeHostCards(cards: unknown): number {
    if (!Array.isArray(cards)) return 0;
    type Entry = { afterOrd: number; ts?: number; ev: { type?: unknown; display?: { mime?: unknown; data?: unknown } } };
    const list = (cards as Entry[])
      .filter((c) => c && typeof c.afterOrd === 'number' && c.ev && typeof c.ev.display?.mime === 'string' && /^application\/vnd\.aisdlc\./.test(c.ev.display.mime))
      .sort((a, b) => a.afterOrd - b.afterOrd);
    let placed = 0;
    for (const c of list) {
      let at = -1;
      let stamped = false;
      this.messages.forEach((m, i) => {
        const o = (m as { srcOrdinal?: unknown }).srcOrdinal;
        if (typeof o !== 'number') return;
        stamped = true;
        if (o < c.afterOrd) at = i;
      });
      if (at < 0 && stamped) continue;
      if (at < 0) at = this.messages.length - 1;
      while (at + 1 < this.messages.length && this.messages[at + 1]!.role === 'aisdlc-card' && Number((this.messages[at + 1] as { afterOrd?: unknown }).afterOrd) <= c.afterOrd) at += 1;
      this.messages.splice(at + 1, 0, { role: 'aisdlc-card', display: { mime: c.ev.display!.mime as string, data: c.ev.display!.data }, frame: String(c.ev.type), inRun: false, replayed: true, afterOrd: c.afterOrd, timestamp: typeof c.ts === 'number' ? c.ts : Date.now() } as PiMessage);
      placed += 1;
    }
    return placed;
  }

  /** In-flight blocks in contentIndex order (for the streaming bubble). */
  inflightBlocks(): PiBlock[] {
    if (!this.inflight) return [];
    return this.inflightBlocksOf(this.inflight);
  }

  /** Same, for an assembly already detached from `this.inflight` — the commit
   * paths null the field before they read it. */
  private inflightBlocksOf(f: Inflight): PiBlock[] {
    return f.order.map((i) => f.blocks.get(i)!).filter(Boolean);
  }

  apply(ev: Record<string, unknown>): PiChange {
    switch (ev.type as string) {
      case 'agent_start':
        this.turnOpen = true;
        this.turnTokens = 0;
        this.turnToolsDone = 0;
        this.verbSeed = (this.verbSeed + 7) % SPINNER_VERBS.length;
        return 'state';
      case 'agent_settled': {
        this.turnOpen = false;
        // A run can settle with tool rows still marked running (abort) —
        // close them so no spinner outlives the run.
        for (const run of this.toolRuns.values()) run.done = true;
        // Settling with an open assembly = the turn was cut off mid-message
        // (Stop / abort). Commit the partial as a SETTLED message stamped
        // stoppedEarly so the answer-so-far stays labeled in the transcript
        // instead of vanishing with the streaming bubble.
        if (this.inflight) {
          const blocks = this.inflightBlocks().map((b) => ({
            ...b,
            streaming: false,
            // A thought cut off mid-stream still gets its measured duration.
            ...(b.type === 'thinking' && b.thinkingMs === undefined && typeof b.thinkingStartedAt === 'number'
              ? { thinkingMs: Date.now() - b.thinkingStartedAt }
              : {}),
          }));
          this.inflight = null;
          if (blocks.length) {
            this.messages.push({ role: 'assistant', content: blocks, stoppedEarly: true });
            return 'messages';
          }
        }
        return 'state';
      }
      case 'turn_refusal':
      case 'turn_failure':
      case 'turn_claim':
      case 'turn_notice': {
        // P22 lane u4 — a Helm-server display frame (the §8f refusal card,
        // turn-ledger.ts; the §8k failure card, failure-card.ts): the host's
        // statement ABOUT a turn, folded as an `aisdlc-card` message that
        // carries NO transcript ordinal, so it is never a turn (aisdlc-cards.ts
        // renders it). `inRun` records whether a run was open when it landed.
        // Inlined, not imported: the smoke transpiles this file standalone.
        const d = ev.display as { mime?: unknown; data?: unknown } | undefined;
        if (!d || typeof d.mime !== 'string' || !/^application\/vnd\.aisdlc\./.test(d.mime)) return 'none';
        this.messages.push({ role: 'aisdlc-card', display: { mime: d.mime, data: d.data }, frame: String(ev.type), inRun: this.turnOpen, timestamp: Date.now() } as PiMessage);
        return 'messages';
      }
      case 'extension_error':
        // Surfaced inline as a transcript row — a run error that only went
        // to a console is a run error the operator never saw.
        this.messages.push({
          role: 'error',
          content: String(ev.error ?? ev.message ?? ev.detail ?? 'extension error'),
        });
        return 'messages';
      case 'message_start': {
        const m = ev.message as PiMessage | undefined;
        if (m?.role === 'assistant') {
          this.inflight = { role: 'assistant', blocks: new Map(), order: [] };
          return 'inflight';
        }
        return 'none'; // user/toolResult land complete at message_end
      }
      case 'message_update':
        return this.applyDelta(ev.assistantMessageEvent as Record<string, unknown> | undefined);
      case 'message_end': {
        const m = ev.message as PiMessage | undefined;
        const open = this.inflight;
        this.inflight = null;
        if (!m) {
          // MEASURED 2026-08-27 (owner screenshots): text streamed visibly and
          // then the WHOLE message disappeared at stream end. `message_end`
          // clears the assembly and pushes only when it carries an
          // authoritative `message` — so an end frame WITHOUT one (a transport
          // hiccup, a truncated turn, a pi build that omits it) deleted the
          // streaming bubble and put nothing in its place. Everything the user
          // just watched arrive vanished on the last frame.
          //
          // agent_settled already handled exactly this for the abort path
          // (below); message_end was its untreated twin. Commit the assembly
          // rather than dropping it: an answer we rendered is an answer we keep.
          const blocks = open
            ? this.inflightBlocksOf(open).map((b) => ({
                ...b,
                streaming: false,
                ...(b.type === 'thinking' && b.thinkingMs === undefined && typeof b.thinkingStartedAt === 'number'
                  ? { thinkingMs: Date.now() - b.thinkingStartedAt }
                  : {}),
              }))
            : [];
          if (blocks.length) {
            this.messages.push({ role: 'assistant', content: blocks });
            return 'messages';
          }
          return 'state';
        }
        // USER ECHO: the one surface that can still carry the wire envelope
        // ("[from Alice] body\n\n<helm-attachments>…") — pi echoes the COMPOSED
        // prompt, and on a lane with no stripping server (the retired console RM
        // lane was one) it arrives raw. Parsed here, at
        // ingest, once — so the live-append render and the durable-transcript
        // reload draw the same clean shape (sender chip + body + attachment
        // chips) instead of the raw string (owner screenshot, 2026-08-31).
        if (m.role === 'user' || isCustomMessage(m)) {
          const incoming = normalizeUserEnvelope(m);
          if (!incoming.length) return 'none'; // a hidden custom message: no row
          // A user echo that merely re-delivers what is already at the tail is
          // the snapshot∩live-buffer overlap the BRIDGE lane has no server
          // id-guard for (helm-bridge stitchDriverTail keys by COUNT, and the
          // pi echo carries no clientMsgId). Skip it rather than double-render
          // (owner defect 2026-09-03: "continue" appearing twice). A legitimate
          // repeated send is never collapsed — the assistant turn between two
          // sends breaks the tail match.
          if (this.duplicatesTail(incoming)) return 'none';
          this.messages.push(...incoming);
          return 'messages';
        }
        // Same failure, second shape: an end frame that DOES carry a message
        // but an empty one. Authoritative-and-empty still blanks a bubble the
        // user watched fill, so prefer the assembly whenever the authoritative
        // copy has nothing in it. (An assistant message that is legitimately
        // empty has no assembly either, so this cannot invent content.)
        if (m.role === 'assistant' && open) {
          const authoritativeEmpty =
            m.content == null ||
            (Array.isArray(m.content) && m.content.length === 0) ||
            (typeof m.content === 'string' && m.content === '');
          const assembled = this.inflightBlocksOf(open);
          if (authoritativeEmpty && assembled.length) {
            this.messages.push({
              ...m,
              content: assembled.map((b) => ({ ...b, streaming: false })),
            });
            this.turnTokens += usageTokens(m.usage);
            return 'messages';
          }
        }
        // message_end.message is authoritative and carries no timing — graft
        // the durations this fold measured onto it by contentIndex.
        if (open && m.role === 'assistant' && Array.isArray(m.content)) {
          for (const [ci, b] of open.blocks) {
            if (b.type !== 'thinking' || typeof b.thinkingMs !== 'number') continue;
            const tgt = (m.content as PiBlock[])[ci];
            if (tgt && tgt.type === 'thinking') tgt.thinkingMs = b.thinkingMs;
          }
        }
        this.messages.push(m);
        if (m.role === 'assistant') this.turnTokens += usageTokens(m.usage);
        return 'messages';
      }
      case 'tool_execution_start': {
        const id = String(ev.toolCallId ?? '');
        if (!id) return 'none';
        this.toolRuns.set(id, {
          toolCallId: id,
          toolName: String(ev.toolName ?? ''),
          args: ev.args,
          done: false,
        });
        return 'tools';
      }
      case 'tool_execution_update': {
        const run = this.toolRuns.get(String(ev.toolCallId ?? ''));
        if (!run) return 'none';
        run.partial = ev.partialResult;
        return 'tools';
      }
      case 'tool_execution_end': {
        const run = this.toolRuns.get(String(ev.toolCallId ?? ''));
        if (!run) return 'none';
        run.done = true;
        run.isError = ev.isError === true;
        run.partial = ev.result ?? run.partial;
        this.turnToolsDone += 1;
        return 'tools';
      }
      case 'queue_update':
        this.queuedSteering = Array.isArray(ev.steering) ? (ev.steering as string[]) : [];
        this.queuedFollowUp = Array.isArray(ev.followUp) ? (ev.followUp as string[]) : [];
        return 'state';
      case 'session_action_update': {
        // prime-agent has no queue_update: its queue rides this event as
        // preview TEXTS (`actions.steering` / `actions.followUps`). A frame
        // without them (count only) leaves the strip as it was.
        const a = ev.actions as { steering?: unknown; followUps?: unknown } | undefined;
        if (!a || !Array.isArray(a.steering) || !Array.isArray(a.followUps)) return 'none';
        this.queuedSteering = a.steering.map((s) => String(s));
        this.queuedFollowUp = a.followUps.map((s) => String(s));
        return 'state';
      }
      case 'compaction_start':
        this.compacting = true;
        return 'state';
      case 'compaction_end':
        this.compacting = false;
        return 'state';
      default:
        return 'none';
    }
  }

  private applyDelta(d: Record<string, unknown> | undefined): PiChange {
    if (!d) return 'none';
    // A delta can arrive before message_start on a mid-stream attach whose
    // replay window began after the start — synthesize the assembly.
    if (!this.inflight) this.inflight = { role: 'assistant', blocks: new Map(), order: [] };
    const idx = Number(d.contentIndex ?? 0);
    const get = (type: PiBlock['type']): PiBlock => {
      let b = this.inflight!.blocks.get(idx);
      if (!b) {
        b = { type, streaming: true };
        this.inflight!.blocks.set(idx, b);
        this.inflight!.order.push(idx);
      }
      return b;
    };
    switch (d.type as string) {
      case 'text_start':
        get('text').text ??= '';
        return 'inflight';
      case 'text_delta': {
        const b = get('text');
        b.text = (b.text ?? '') + String(d.delta ?? '');
        return 'inflight';
      }
      case 'text_end': {
        const b = get('text');
        if (typeof d.content === 'string') b.text = d.content; // authoritative
        b.streaming = false;
        return 'inflight';
      }
      case 'thinking_start': {
        const b = get('thinking');
        b.thinking ??= '';
        b.thinkingStartedAt ??= Date.now();
        return 'inflight';
      }
      case 'thinking_delta': {
        const b = get('thinking');
        b.thinking = (b.thinking ?? '') + String(d.delta ?? '');
        return 'inflight';
      }
      case 'thinking_end': {
        const b = get('thinking');
        if (typeof d.content === 'string') b.thinking = d.content;
        if (b.thinkingMs === undefined && typeof b.thinkingStartedAt === 'number') {
          b.thinkingMs = Date.now() - b.thinkingStartedAt;
        }
        b.streaming = false;
        return 'inflight';
      }
      case 'toolcall_start': {
        const b = get('toolCall');
        b.argsRaw ??= '';
        return 'inflight';
      }
      case 'toolcall_delta': {
        const b = get('toolCall');
        b.argsRaw = (b.argsRaw ?? '') + String(d.delta ?? '');
        return 'inflight';
      }
      case 'toolcall_end': {
        const b = get('toolCall');
        b.toolCall = d.toolCall as PiToolCall | undefined;
        b.streaming = false;
        return 'inflight';
      }
      default:
        return 'none';
    }
  }
}

/** Text of a message's content, whatever shape the role uses. */
export function contentText(content: unknown): string {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .map((b) => {
        const blk = b as PiBlock & { text?: string };
        if (blk.type === 'text') return blk.text ?? '';
        return '';
      })
      .join('');
  }
  return '';
}

/** Output-token count out of a pi usage record, across the key spellings pi
 * versions have used. Returns 0 when absent — the meta line omits zeros. */
export function usageTokens(usage: unknown): number {
  if (typeof usage !== 'object' || usage === null) return 0;
  const rec = usage as Record<string, unknown>;
  for (const key of ['output', 'outputTokens', 'output_tokens']) {
    const v = rec[key];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return 0;
}

/** ONE clock format for every transcript surface (owner ruling 2026-09-02):
 * today → "4:58 PM"; yesterday → "Yesterday at 4:58 PM"; older → "<Date> at
 * <Time>" (month + day, year appended only when it differs from now's).
 * `opts.seconds` keeps the step-row precision ("4:58:47 PM" — steps usually
 * land within one minute). `opts.todayWord` prefixes the same-day branch
 * ("Today 12:48 PM" — the bookmark surfaces name the day explicitly; the
 * transcript keeps the bare time). `opts.now` is injectable so the branches
 * are testable facts, not clock races. */
export function formatChatTime(
  ts: number,
  opts: { seconds?: boolean; now?: number; todayWord?: boolean } = {},
): string {
  const now = typeof opts.now === 'number' ? opts.now : Date.now();
  // Defensive: pi stamps epoch ms; treat small values as seconds.
  const ms = ts < 1e12 ? ts * 1000 : ts;
  const d = new Date(ms);
  const n = new Date(now);
  const time = d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    ...(opts.seconds ? { second: '2-digit' } : {}),
    hour12: true,
  });
  const sameDay = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(d, n)) return opts.todayWord ? `Today ${time}` : time;
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (sameDay(d, yesterday)) return `Yesterday at ${time}`;
  const date = d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() === n.getFullYear() ? {} : { year: 'numeric' }),
  });
  return `${date} at ${time}`;
}

/** "4m 51s" */
export function fmtElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
}

/** "13.7k" */
export function fmtTokens(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

/** The working line — `✳ Improvising… (4m 51s · ↑ 13.7k tokens · 3 tools)`.
 * Verb cycles every ~4s from the model's per-turn seed (replay-stable for a
 * given elapsed window); 'Compacting' overrides while a compaction runs.
 * DOM-free so the smoke lane can pin the composition. */
export function composeWorkingLine(m: PiLiveModel, elapsedMs: number): { verb: string; meta: string } {
  const verb = m.compacting
    ? 'Compacting'
    : SPINNER_VERBS[(m.verbSeed + Math.floor(Math.max(0, elapsedMs) / 4000)) % SPINNER_VERBS.length]!;
  const parts: string[] = [fmtElapsed(elapsedMs)];
  if (m.turnTokens > 0) parts.push(`↑ ${fmtTokens(m.turnTokens)} tokens`);
  if (m.turnToolsDone > 0) parts.push(`${m.turnToolsDone} tool${m.turnToolsDone === 1 ? '' : 's'}`);
  return { verb, meta: parts.join(' · ') };
}

/** One-line summary of a tool call's arguments for the tool row header. */
export function summarizeArgs(args: unknown): string {
  if (args == null) return '';
  try {
    const obj = typeof args === 'string' ? (JSON.parse(args) as unknown) : args;
    if (typeof obj === 'object' && obj !== null) {
      const rec = obj as Record<string, unknown>;
      for (const key of ['command', 'path', 'file_path', 'pattern', 'name', 'title', 'to', 'query', 'message']) {
        const v = rec[key];
        if (typeof v === 'string' && v.trim()) return v.length > 120 ? `${v.slice(0, 119)}…` : v;
      }
      const s = JSON.stringify(obj);
      return s.length > 120 ? `${s.slice(0, 119)}…` : s;
    }
    return String(obj);
  } catch {
    const s = String(args);
    return s.length > 120 ? `${s.slice(0, 119)}…` : s;
  }
}

// ---- wire-envelope parse (operator-facing) ---------------------------------
//
// The prompt an AGENT receives is a composed wire envelope: queued sends
// joined with per-sender "[from <name>] " prefixes, plus one trailing fenced
// attachment manifest. That envelope exists FOR the model; a person must never
// read it. The Bridge strips the fence server-side at ingest
// (pi-stack/server/attachments.ts rewriteEvent), but the retired (P16) console
// RM lane's live echo and its S3-backfilled history reached the browser raw —
// and stored history still can — so the model parses at
// INGEST here, once, rather than asking every render surface (live append,
// snapshot, queued morph) to remember. Two of them forgot; that is the defect
// this section retires.
//
// GRAMMAR SYNC: the fence tags and the "[Kind #N]" label grammar are minted by
// pi-stack/src/server/attachments.ts (appendManifest); the second minter,
// helm-console's RM attach-bytes.ts (appendAttachmentBlock), retired with the
// release manager (P16) and its shape survives only in stored history. The
// smoke lane pins the mint→parse round-trip so drift is a red check, not a
// silent render regression.

const WIRE_FENCE_OPEN = '<helm-attachments>';
const WIRE_FENCE_CLOSE = '</helm-attachments>';
/** Anchored at the END of the message: a fence-looking string mid-prose is
 * operator text and stays untouched (mirror of the server's trailing RE). */
const WIRE_FENCE_TRAILING_RE = new RegExp(`\\n*${WIRE_FENCE_OPEN}\\n[\\s\\S]*?\\n${WIRE_FENCE_CLOSE}\\s*$`);
/** "[from <display name>] " — minted by the drain lanes, never multi-line. */
const WIRE_FROM_RE = /^\[from ([^\]\n]{1,200})\] /;
const WIRE_FROM_SPLIT_RE = /\n\n(?=\[from [^\]\n]{1,200}\] )/;
/** Manifest entry head — one label grammar for every minter, live or retired. */
const WIRE_ENTRY_RE = /^\[(Image|File|Folder) #(\d+)\] (.*)$/;
/** Trailing reply-context fence (owner item 54) — GRAMMAR SYNC with
 * server/attachments.ts (appendReplyContext/splitReplyContext); the smoke
 * lane pins the mint→parse round-trip. Parsed here for the raw-wire lanes
 * (queued ghosts, and the retired RM lane's stored echoes); the Bridge lane
 * arrives pre-lifted. */
const WIRE_REPLY_TRAILING_RE = /\n*<helm-reply-to ord="(\d{1,9})">\n([\s\S]*?)\n<\/helm-reply-to>\s*$/;

/** Split one message text into what the person typed and its reply reference.
 * No trailing fence ⇒ byte-identical text, reply null. */
export function splitReplyText(raw: string): { text: string; reply: { ord: number; text: string } | null } {
  const text = String(raw ?? '');
  const m = WIRE_REPLY_TRAILING_RE.exec(text);
  if (!m) return { text, reply: null };
  const ord = Number(m[1]);
  if (!Number.isInteger(ord) || ord < 0) return { text, reply: null };
  return { text: text.slice(0, m.index).trimEnd(), reply: { ord, text: m[2] ?? '' } };
}

export interface WireEnvelopeSection {
  sender: string | null;
  text: string;
}
export interface ParsedWireEnvelope {
  sections: WireEnvelopeSection[];
  attachments: PiAttachmentBlock[];
}

/**
 * Parse one wire-composed user text back into what a person should read:
 * per-sender sections and structured attachment blocks. Returns null when the
 * text carries neither a trailing fence nor a leading "[from …]" prefix —
 * identity is the no-op signal (same contract as the server's rewriteMessage),
 * so an ordinary typed message is never reinterpreted.
 */
export function parseWireEnvelope(raw: string): ParsedWireEnvelope | null {
  const text = String(raw ?? '');
  const fence = WIRE_FENCE_TRAILING_RE.exec(text);
  const attachments: PiAttachmentBlock[] = [];
  if (fence) {
    const lines = fence[0].split('\n');
    for (let i = 0; i < lines.length; i++) {
      const head = WIRE_ENTRY_RE.exec(lines[i] ?? '');
      if (!head) continue;
      const rest = head[3] ?? '';
      // Entries minted by the retired (P16) RM lane append delivery metadata
      // after an em-dash
      // ("name — application/zip, 415225 bytes, saved …"); the Bridge shape is
      // the bare name (its indented "  path:" line never matches ENTRY_RE).
      // Either way the NAME is what the chip shows.
      const dash = rest.indexOf(' — ');
      const name = (dash >= 0 ? rest.slice(0, dash) : rest).trim();
      const meta = dash >= 0 ? rest.slice(dash + 3) : '';
      // A text file inlined into the fence ends its entry line with
      // "contents below:" and carries the body between begin/end markers —
      // skip the body wholesale so file CONTENTS cannot forge entries.
      if (/contents below:$/.test(rest)) {
        while (i + 1 < lines.length && !/^--- end /.test(lines[i + 1] ?? '')) i++;
        i++; // the "--- end … ---" line itself
      }
      attachments.push({
        type: 'attachment',
        label: `${head[1]} #${head[2]}`,
        name: name || 'attachment',
        kind: head[1] === 'Image' ? 'image' : head[1] === 'Folder' ? 'folder' : 'file',
        mime: /^[\w.+-]+\/[\w.+-]+/.exec(meta)?.[0] ?? '',
        url: '', // not addressable from an echo — the chip renders unclickable
      });
    }
  }
  const body = fence ? text.slice(0, fence.index).trimEnd() : text;
  // Sections split ONLY when the message OPENS with a prefix: the drain lanes
  // prefix every section or none, so a mid-prose "\n\n[from x] " in an
  // unprefixed message is the operator's own text.
  const sections: WireEnvelopeSection[] = WIRE_FROM_RE.test(body)
    ? body.split(WIRE_FROM_SPLIT_RE).map((part) => {
        const m = WIRE_FROM_RE.exec(part);
        return m ? { sender: m[1] ?? null, text: part.slice(m[0].length) } : { sender: null, text: part };
      })
    : [{ sender: null, text: body }];
  if (!fence && sections.length === 1 && sections[0]!.sender === null) return null;
  return { sections, attachments };
}

// ---- pi's `custom` message → the user row ----------------------------------
//
// pi records an extension's `sendMessage({ customType, content, display,
// details })` as a `custom_message` FILE entry and replays it into context as
// `{ role: "custom", customType, content, display, details, timestamp }`
// (pi-coding-agent 0.84.1: session-manager.js appendCustomMessageEntry,
// messages.js createCustomMessage). The coordination extension drains a
// NON-RESIDENT lead's inbox exactly that way at turn start, so every message a
// person sends to a scope whose lead has to be woken lands in the session as a
// custom message and NOT as `role: "user"`.
//
// Until this seat existed every transcript reader kept only `type: "message"`
// (file) / `role: "user"` (row builder), so the person's own sends were the
// rows missing from their transcript. Measured on a live VM session file:
// 41 entries — session 1, model_change 1, thinking_level_change 1,
// custom_message 4 (all coordination-inbox, display true), message 34
// (assistant 14, toolResult 20, user 0) — served as 34/34 with `hasMore:false`
// and the 4 sends absent, and re-rendered without them on every detach.
//
// `display` is pi's own "show this in the TUI" bit: a displayed custom is a
// user row here; a hidden one (a persona / goal-context injection) stays
// hidden here exactly as it does there.
//
// ONE predicate, consumed by the server file reader (helm-bridge
// transcript.ts piEntryMessage), the attach seam (helm-bridge sessions.ts
// stitchDriverTail), the live fold below (normalizeUserEnvelope) — so a page
// read from the file and a page stitched from the driver's cache count the
// same rows. It lives HERE and not in a sibling module because the smoke
// transpiles this file (and pi-steps.ts) STANDALONE into a temp dir, where a
// sibling import cannot resolve (traps: instrument-copied-standalone-into-a-
// fixture); pi-steps.ts inlines the two-line rule for the same reason.

export interface PiCustomMessage {
  role: 'custom';
  customType?: unknown;
  content?: unknown;
  display?: unknown;
  details?: unknown;
  timestamp?: unknown;
  [k: string]: unknown;
}

/** The user row a displayed custom message projects to. `customType` and
 * `details` ride along (the drain's `{ scope, files }`) so a renderer can
 * tell an inbox drain from a typed send without re-parsing the text. */
export interface CustomUserRow {
  role: 'user';
  content: unknown;
  timestamp?: number;
  customType: string;
  details?: unknown;
  /** Structural overlap with pi-live's PiMessage (an open record). */
  [k: string]: unknown;
}

/** prime-agent's continual-harness refinement result (customType
 * `refinement_outcome`, the fork's REFINEMENT_OUTCOME_CUSTOM_TYPE) — the
 * HARNESS talking, never a person: no envelope parse, no sender, no
 * attribution, not an operator turn. Same rule as pi-steps isHarnessRefined
 * (inlined: both files are transpiled standalone by the smoke;
 * harness-refined-card.test.mjs pins the two equal). */
export function isHarnessRefinement(m: unknown): boolean {
  return !!m && typeof m === 'object' && (m as { customType?: unknown }).customType === 'refinement_outcome';
}

/** The fork's other DISPLAYED outcome customs — compaction outcome, MCP
 * connection outcome, slash-command result. The harness reporting, never a
 * person, exactly like the refinement result. Same set as pi-steps
 * SYSTEM_OUTCOME_TITLES (inlined: both files are transpiled standalone by the
 * smoke; system-outcome-card.test.mjs pins the two equal). */
export const SYSTEM_OUTCOME_CUSTOM_TYPES: readonly string[] = [
  'compaction_outcome',
  'mcp_connection_outcome',
  'session_slash_command_result',
];

export function isSystemOutcomeMessage(m: unknown): boolean {
  if (!m || typeof m !== 'object') return false;
  const t = (m as { customType?: unknown }).customType;
  return typeof t === 'string' && SYSTEM_OUTCOME_CUSTOM_TYPES.includes(t);
}

/** Any message the Helm renders as a harness card (the refinement result or a
 * system outcome): no envelope parse, no sender, no attribution, not an
 * operator turn. */
export function isHarnessCardMessage(m: unknown): boolean {
  return isHarnessRefinement(m) || isSystemOutcomeMessage(m) || isSystemNoticeMessage(m);
}

/** prime-agent's TUI rule (owner ruling 2026-10-01): a DISPLAYED custom
 * message is never a user message — it is a third category, rendered inline
 * in the agent's stream as a system notice (the fork's interactive-mode
 * addMessageToChat: role "custom" never reaches UserMessageComponent).
 * Projection to a user-role row is only how our readers carry it; the
 * customType it keeps is the marker. Exempt: customs that CARRY A PERSON
 * (the coordination drain of people's sends; the person's own typed slash
 * command), agent_message (its own sender-attributed renderer), and the
 * harness cards above. A row with a sender or coord attrs is a person's.
 * Same rule as pi-steps isSystemNotice (inlined: both files are transpiled
 * standalone by the smoke; system-notice.test.mjs pins the two equal). */
export const SYSTEM_NOTICE_EXEMPT_CUSTOM_TYPES: readonly string[] = [
  'coordination-inbox',
  'session_slash_command',
  'agent_message',
];

export function isSystemNoticeMessage(m: unknown): boolean {
  if (!m || typeof m !== 'object') return false;
  const r = m as { role?: unknown; customType?: unknown; sender?: unknown; coord?: unknown };
  if (r.role !== 'user' && r.role !== 'custom') return false;
  const t = r.customType;
  if (typeof t !== 'string' || !t || r.sender || r.coord) return false;
  return !SYSTEM_NOTICE_EXEMPT_CUSTOM_TYPES.includes(t) && !isHarnessRefinement(m) && !isSystemOutcomeMessage(m);
}

export function isCustomMessage(m: unknown): m is PiCustomMessage {
  return !!m && typeof m === 'object' && (m as { role?: unknown }).role === 'custom';
}

/** pi stamps the file ENTRY with an ISO string and the replayed message with
 * epoch ms (`new Date(timestamp).getTime()`); a user row carries ms. */
export function customTimestampMs(t: unknown): number | undefined {
  if (typeof t === 'number') return Number.isFinite(t) ? t : undefined;
  if (typeof t === 'string' && t) {
    const ms = Date.parse(t);
    return Number.isFinite(ms) ? ms : undefined;
  }
  return undefined;
}

/** The user row a DISPLAYED custom message renders as; null for a hidden one
 * (`display !== true` — pi's default for the bit is hidden). */
export function projectCustom(m: PiCustomMessage): CustomUserRow | null {
  if (m.display !== true) return null;
  const ts = customTimestampMs(m.timestamp);
  // An agent-to-agent message carries its sender in `details`: the row wears
  // the agent-sender chip and its body is the payload, the header gone.
  const a2a = agentMessageOf(m);
  return {
    role: 'user',
    content: a2a ? a2a.text : (m.content ?? ''),
    ...(ts !== undefined ? { timestamp: ts } : {}),
    customType: typeof m.customType === 'string' ? m.customType : String(m.customType ?? ''),
    ...(m.details !== undefined ? { details: m.details } : {}),
    ...(a2a ? { sender: a2a.sender } : {}),
  };
}

// ---- prime-agent agent-to-agent messages ------------------------------------
//
// prime-agent delivers a message from another agent (a child helper's report,
// a parent's task or steer, a sibling) as a DISPLAYED custom message:
// `{ role: 'custom', customType: 'agent_message', content: '[agent-message
// from child:<name>]\n\n<message>', details: { id, message, from,
// fromRelationship, target } }` — the fork's createAgentSessionMessage
// (core/agent-messages.ts); a spawn's task is the same type with
// content '[task from parent]\n\n<task>' and id 'spawn:<run>'. Without this
// seat the row projected to an anonymous USER bubble with the bracket header
// as its first line. The marker is the custom ROLE plus its customType and
// details — the harness mints those, a person's send is always role 'user'
// with neither — so a typed message that merely STARTS with
// '[agent-message from …' is never re-labelled (the text prefix is not read).
// The display name is details.from.sessionName; with none, the relationship
// word ('parent' …) — never the raw session id the header falls back to.

export const AGENT_MESSAGE_CUSTOM_TYPE = 'agent_message';

/** The relationships the fork records (AgentFamilyRelationship) plus 'peer'
 * for a sender that named none (a top-level session messaging another). */
export type AgentMessageRole = 'child' | 'parent' | 'sibling' | 'peer';

/** The words the chip's role tag reads, one per relationship. */
export const AGENT_MESSAGE_ROLE_LABELS: Readonly<Record<AgentMessageRole, string>> = {
  child: 'helper',
  parent: 'parent',
  sibling: 'sibling',
  peer: 'peer session',
};

/** Sender + header-free body of a prime-agent agent-to-agent message, or null
 * for anything else (any other custom, a malformed details, a user row). */
export function agentMessageOf(m: unknown): { sender: PiSenderRef; text: string } | null {
  if (!m || typeof m !== 'object') return null;
  const r = m as { role?: unknown; customType?: unknown; details?: unknown };
  if (r.role !== 'custom' || r.customType !== AGENT_MESSAGE_CUSTOM_TYPE) return null;
  const d = r.details as { id?: unknown; message?: unknown; from?: unknown; fromRelationship?: unknown } | null;
  if (!d || typeof d !== 'object' || typeof d.id !== 'string' || typeof d.message !== 'string') return null;
  const rel = d.fromRelationship;
  const role: AgentMessageRole = rel === 'child' || rel === 'parent' || rel === 'sibling' ? rel : 'peer';
  const from = d.from as { sessionName?: unknown } | null | undefined;
  const named = from && typeof from === 'object' && typeof from.sessionName === 'string' ? from.sessionName.trim() : '';
  const name = named ? named.slice(0, 120) : role === 'peer' ? 'agent' : role;
  return { sender: { userId: null, name, kind: 'agent', role }, text: d.message };
}

/** A message list as the transcript counts it: displayed customs projected,
 * hidden ones dropped, every other message as-is. */
export function transcriptRowsOf(messages: readonly unknown[]): unknown[] {
  const out: unknown[] = [];
  for (const m of messages) {
    if (!isCustomMessage(m)) {
      out.push(m);
      continue;
    }
    const row = projectCustom(m);
    if (row) out.push(row);
  }
  return out;
}

/**
 * Split ONE user message back into person-shaped rows. Identity ([m]) when
 * there is nothing to parse; a message that already carries a sender (a
 * durable-transcript row) is never second-guessed — which is what keeps the
 * reload path byte-identical.
 *
 * A multi-sender composed echo becomes one row per section (matching the
 * durable transcript's one-row-per-send); non-text blocks (inline images)
 * ride the first row, and the fence's chips ride the last — the fence sits at
 * the end of the composed prompt and cannot say which send owned which file.
 */
export function normalizeUserEnvelope(m: PiMessage): PiMessage[] {
  // A `custom` message (an extension's sendMessage — the coordination
  // extension's inbox drain to a NON-RESIDENT lead) rides the SAME parser:
  // a displayed one is the user row it projects to, a hidden one is no row
  // at all (custom-row.ts; the server file reader applies the identical
  // predicate, so reload and live-append cannot disagree on it).
  if (isCustomMessage(m)) {
    const row = projectCustom(m);
    if (row && isHarnessCardMessage(row)) return [row as PiMessage];
    return row ? normalizeUserEnvelope(row as PiMessage) : [];
  }
  // The RELOAD shape of a harness card (the server file reader already
  // projected it to a user-role row that keeps its customType): identity too.
  if (!m || m.role !== 'user' || m.sender || isHarnessCardMessage(m)) return [m];
  const content = m.content;
  let text: string;
  let extra: unknown[] = [];
  if (typeof content === 'string') {
    text = content;
  } else if (Array.isArray(content)) {
    text = content
      .filter((b) => (b as PiBlock | null)?.type === 'text')
      .map((b) => (b as PiBlock).text ?? '')
      .join('');
    extra = content.filter((b) => (b as PiBlock | null)?.type !== 'text');
  } else {
    return [m];
  }
  // An inbox drain wraps each wake in `<coordination-inbox …>`; the body
  // inside is the wire text the Bridge composed (`[from <name>] …`), so
  // unwrapping first lets the sender chip and the per-section split below
  // read a drained send exactly as they read a direct one.
  const unwrapped = unwrapCoordinationInbox(text);
  const unwrappedOnly = unwrapped !== text;
  text = unwrapped;
  const parsed = parseWireEnvelope(text);
  if (!parsed) {
    // A plain (unlabeled, fence-less) send can still carry the trailing
    // reply-context fence — lift it into the structured field the pill
    // renders. A message already lifted server-side never re-matches
    // (its text is clean), so this cannot double-apply.
    const rep = splitReplyText(text);
    if (rep.reply && !m.replyTo) {
      return [
        {
          ...m,
          content: extra.length
            ? [...(rep.text ? [{ type: 'text', text: rep.text }] : []), ...extra]
            : rep.text,
          replyTo: rep.reply,
        },
      ];
    }
    // Not a composed wire envelope — but injected non-human messages carry
    // their own marker shapes (coordination pushes, inbox drains, dispatch
    // resume prompts). One parse, same renderer, same chip.
    const inj = parseInjectionMarker(text);
    if (!inj) {
      // Unwrapped but otherwise plain (an unlabeled send): the body renders,
      // never the envelope markup.
      return unwrappedOnly ? [{ ...m, content: extra.length ? [{ type: 'text', text }, ...extra] : text }] : [m];
    }
    return [
      {
        ...m,
        content: extra.length ? [{ type: 'text', text: inj.text }, ...extra] : inj.text,
        sender: inj.sender,
        ...(inj.coordAttrs ? { coord: inj.coordAttrs } : {}),
      },
    ];
  }
  const out: PiMessage[] = [];
  parsed.sections.forEach((s, i) => {
    // Per-SECTION reply lift: a multi-sender drain joins parts with '\n\n'
    // and each part may carry its own trailing reply fence, which the
    // section split restores to a per-section trailing position.
    const rep = splitReplyText(s.text);
    const blocks: unknown[] = [];
    if (rep.text) blocks.push({ type: 'text', text: rep.text });
    if (i === 0) blocks.push(...extra);
    if (i === parsed.sections.length - 1) blocks.push(...parsed.attachments);
    if (!blocks.length && !rep.reply) return; // an empty section renders nothing
    out.push({
      ...m,
      content: blocks,
      ...(s.sender ? { sender: senderRefFor(s.sender) } : {}),
      ...(rep.reply ? { replyTo: rep.reply } : {}),
    });
  });
  return out.length ? out : [m];
}

/** One queued-send item as the view renders it (the `pi-queue` frame). */
export interface PiQueueItem {
  id: string;
  text: string;
  sender: { userId: string | null; name: string; pictureUrl?: string | null };
  ts: number;
  /** The transcript id this send was accepted under — a bubble whose id is
   * already in the transcript has been delivered and must not render. */
  clientMsgId?: string;
  /** Attachment blocks, same shape the delivered row carries. */
  attachments?: unknown[];
  /** Reply-to reference — the queued bubble wears the SAME pill the landed
   * row will (one visual contract, never a lookalike). */
  replyTo?: { ord: number; text: string };
  /** Console RM lane (2026-09-07; the lane retired in P16): the supervisor's
   * re-kick ladder PARKED this row (terminal cause / exhausted ladder). Still
   * the user's message, still cancellable; re-armed by the next send. Absent
   * = live. No live server sends it now. */
  state?: 'dead';
  deadReason?: string;
}

/**
 * Normalize a `pi-queue` frame's items across the two server dialects: the
 * Bridge sends `sender: {userId, name}` plus attachment BLOCKS; the console's
 * RM lane (retired in P16) historically sent `sender: "<name>"` with `userId` beside it and no
 * attachments — which is exactly why the RM queued bubble rendered senderless
 * and chipless while the delivered row did not.
 */
export function normalizeQueueItems(items: unknown): PiQueueItem[] {
  if (!Array.isArray(items)) return [];
  return items.map((raw) => {
    const it = (raw ?? {}) as Record<string, unknown>;
    const s = it.sender;
    const pic = (o: { pictureUrl?: unknown }): string | null =>
      typeof o.pictureUrl === 'string' ? o.pictureUrl : null;
    const sender =
      s && typeof s === 'object'
        ? {
            userId: typeof (s as { userId?: unknown }).userId === 'string' ? String((s as { userId?: unknown }).userId) : null,
            name: String((s as { name?: unknown }).name ?? 'unknown'),
            // Avatar parity (item-36 amendment defect): the queued bubble and
            // the landed row are one message, so the queued chip carries the
            // same OIDC picture the landed row does — not initials while
            // queued, the real face once landed.
            ...(pic(s as { pictureUrl?: unknown }) ? { pictureUrl: pic(s as { pictureUrl?: unknown }) } : {}),
          }
        : {
            userId: typeof it.userId === 'string' ? it.userId : null,
            name: typeof s === 'string' && s ? s : 'unknown',
            ...(pic(it) ? { pictureUrl: pic(it) } : {}),
          };
    const rawReply = it.replyTo as { ord?: unknown; text?: unknown } | undefined;
    const replyTo =
      rawReply && typeof rawReply === 'object' && typeof rawReply.ord === 'number' && Number.isInteger(rawReply.ord) && rawReply.ord >= 0
        ? { ord: rawReply.ord, text: String(rawReply.text ?? '') }
        : undefined;
    return {
      id: String(it.id ?? ''),
      text: String(it.text ?? ''),
      sender,
      ts: typeof it.ts === 'number' ? it.ts : 0,
      ...(typeof it.clientMsgId === 'string' ? { clientMsgId: it.clientMsgId } : {}),
      ...(Array.isArray(it.attachments) ? { attachments: it.attachments } : {}),
      ...(replyTo ? { replyTo } : {}),
      ...(it.state === 'dead'
        ? { state: 'dead' as const, ...(typeof it.deadReason === 'string' ? { deadReason: it.deadReason } : {}) }
        : {}),
    };
  });
}

// ---- injected-message markers ----------------------------------------------
//
// Non-human actors inject prompts that land as `role:'user'` rows: the
// coordination extension wraps pushes in a `<coordination …>` block, the
// agent-teams inbox drain frames "📨 [inbox] message from X", a dispatch
// resume opens with "[Message from X]", and the Bridge's own plumbing labels
// itself "[bridge]". Every one of those already NAMES its sender in the text —
// this parse lifts that name into the structured sender the chip renders,
// instead of leaving an anonymous bubble of raw markup. Shapes are pinned to
// their minters:
//   pi-shared/extensions/coordination/index.ts (the <coordination> block,
//     attrs JSON-quoted from push-format.ts meta),
//   pi-shared/extensions/agent-teams/inbox-drain.ts (the 📨 frame),
//   pi-shared/extensions/agent-teams/dispatch.ts (the [Message from …] frame),
//   helm-bridge/src/server/compact-executor.ts ("[bridge] auto-compact: …").
// A text that matches none of them returns null and renders untouched — the
// legacy/anonymous row is the control case, never an error.

const INJ_COORD_RE = /^<coordination ([^>\n]*)>\n([\s\S]*)\n<\/coordination>\s*$/;
const INJ_INBOX_RE = /^📨 \[inbox(?: ([^\]\n]{1,80}))?\] message from ([^\n]{1,120}?)(?: — ([^\n]*))?:\n\n([\s\S]*)$/;
const INJ_DISPATCH_RE = /^\[Message from ([^\]\n]{1,120})\](?: \([^)\n]*\))?\n\n([\s\S]*)$/;
const INJ_BRIDGE_RE = /^\[bridge\] /;
/** One drained wake as the coordination extension presents it (producer-only
 * drainScopeInbox): `<coordination-inbox scope=… file=… [at=…] age=…>`, the
 * wake's message verbatim, `</coordination-inbox>`; N wakes join with a blank
 * line. Attributes are quoted JSON strings, so `>` cannot appear unescaped. */
const INJ_COORD_INBOX_RE = /^<coordination-inbox ([^>\n]*)>\n([\s\S]*?)\n<\/coordination-inbox>\s*$/;

/** The wake bodies of a drain, joined as the wire grammar joins sends
 * (`\n\n`), so `[from <name>] …` sections split and chip as usual. Text that
 * is not a drain end to end (any section failing the grammar) is returned
 * verbatim — this never edits an ordinary message. */
export function unwrapCoordinationInbox(text: string): string {
  if (!text.startsWith('<coordination-inbox ')) return text;
  const bodies: string[] = [];
  for (const part of text.split(/\n\n(?=<coordination-inbox )/)) {
    const m = INJ_COORD_INBOX_RE.exec(part);
    if (!m) return text;
    bodies.push(m[2] ?? '');
  }
  return bodies.join('\n\n');
}

export interface ParsedInjection {
  sender: PiSenderRef;
  /** What the bubble should read — the marker's framing stripped where it
   * only repeated the sender, kept where it is part of the message. */
  text: string;
  /** `<coordination>` lane only: the envelope's parsed attrs, preserved onto
   * PiMessage.coord so downstream renderers keep the structured event after
   * the markup is stripped. */
  coordAttrs?: Record<string, string>;
}

/** Attr parse for the <coordination k="…" …> head: values are JSON strings
 * (the minter uses JSON.stringify), so quoted escapes round-trip. */
function coordAttrs(head: string): Record<string, string> {
  const out: Record<string, string> = {};
  const re = /([a-zA-Z_]+)=("(?:[^"\\]|\\.)*")/g;
  for (let m = re.exec(head); m; m = re.exec(head)) {
    try {
      out[m[1]!] = String(JSON.parse(m[2]!));
    } catch {
      /* a malformed attr names nothing — the rest still parse */
    }
  }
  return out;
}

export function parseInjectionMarker(raw: string): ParsedInjection | null {
  const text = String(raw ?? '');
  const coord = INJ_COORD_RE.exec(text);
  if (coord) {
    const attrs = coordAttrs(coord[1] ?? '');
    const from = attrs.from?.trim();
    // A layer-minted event (kind 'coordination', or no `from` at all) is the
    // coordination layer itself; anything with a `from` is a peer agent.
    if (!from || attrs.kind === 'coordination') {
      return {
        sender: { userId: null, name: 'coordination layer', kind: 'coordination', key: 'system' },
        text: coord[2] ?? '',
        coordAttrs: attrs,
      };
    }
    return {
      sender: {
        userId: null,
        name: from,
        kind: 'agent',
        // Channels grammar: a cross-scope sender's face IS its scope key;
        // otherwise the host derives scope§name from the session it renders in.
        ...(attrs.from_scope ? { key: attrs.from_scope } : {}),
      },
      text: coord[2] ?? '',
      coordAttrs: attrs,
    };
  }
  const inbox = INJ_INBOX_RE.exec(text);
  if (inbox) {
    const channel = inbox[1];
    return {
      sender: { userId: null, name: (inbox[2] ?? '').trim(), kind: 'agent' },
      text: `${channel ? `[${channel}] ` : ''}${inbox[4] ?? ''}`,
    };
  }
  const dispatch = INJ_DISPATCH_RE.exec(text);
  if (dispatch) {
    return {
      sender: { userId: null, name: (dispatch[1] ?? '').trim(), kind: 'agent' },
      text: dispatch[2] ?? '',
    };
  }
  if (INJ_BRIDGE_RE.test(text)) {
    return {
      sender: { userId: null, name: 'bridge', kind: 'system', key: 'bridge' },
      text: text.replace(INJ_BRIDGE_RE, ''),
    };
  }
  return null;
}

/** Sender ref for a bare display name (the `[from …]` grammar carries no
 * kind). The two FRAMEWORK actors that label themselves through that grammar
 * map onto their stable identicon keys; every other name is a person. */
export function senderRefFor(name: string): PiSenderRef {
  if (name === 'coordination layer') return { userId: null, name, kind: 'coordination', key: 'system' };
  if (name === 'bridge') return { userId: null, name, kind: 'system', key: 'bridge' };
  return { userId: null, name };
}

// ---- queued-message rendering (owner item 36) -------------------------------
//
// A queued message and a landed message must be visually IDENTICAL — position
// is the only difference. Both therefore reduce to the ONE PiMessage shape the
// user-row builder renders, through the ONE parser (normalizeUserEnvelope):
// a pi steering/follow-up ghost is the exact wire text pi will later echo as
// the landed user message, so parsing it with the same function makes drift
// structurally impossible rather than merely tested.

/** The label half of the sender line, its own copy-seat so the chip can put
 * the avatar BETWEEN the label and the name (owner order 2026-09-03:
 * "Message from:" [avatar] <Username>) without forking the wording. */
export const SENDER_LINE_LABEL = 'Message from:';

/** The sender line every chat surface renders above an attributed message.
 * One copy of the format (owner wording): "Message from: <Username>" —
 * composed from SENDER_LINE_LABEL so the split chip cannot drift from it. */
export function senderLineFor(name: string): string {
  return `${SENDER_LINE_LABEL} ${name}`;
}

/** Owner amendment to item 36: only messages from NON-HUMAN actors render
 * with the serif transcript font + markdown; a human's message shows exactly
 * what they typed, plain, in the composer's font. Classification rides the
 * sender's KIND — the source lanes stamp it (injection markers and the
 * framework actors carry 'agent'/'coordination'/'system'; humans are 'user'
 * or unmarked) — never a string heuristic on the display name. No sender at
 * all (the solo local composer) is a human. */
export function isAgentSender(sender?: PiSenderRef): boolean {
  return sender?.kind === 'agent' || sender?.kind === 'coordination' || sender?.kind === 'system';
}

/** A supervisor-held queue item (pendingSends / rm_turn_queue rows) as the
 * PiMessage the landed row will become — same role, same content blocks,
 * same sender ref, so the queued bubble IS a user row, not a lookalike.
 * The synthetic solo identity stays senderless (chipless), exactly like its
 * landed row. */
export function queueItemMessage(item: PiQueueItem): PiMessage {
  const blocks: unknown[] = [];
  if (item.text) blocks.push({ type: 'text', text: item.text });
  if (item.attachments?.length) blocks.push(...item.attachments);
  return {
    role: 'user',
    content: blocks.length === 1 && item.text ? item.text : blocks,
    // The send instant — the queued bubble shows its own timestamp exactly
    // like the landed row will (owner ruling 2026-09-02: every sent message
    // shows when it went through).
    ...(item.ts > 0 ? { timestamp: item.ts } : {}),
    // Reply pill parity: queued and landed are one message, one pill.
    ...(item.replyTo ? { replyTo: item.replyTo } : {}),
    ...(item.sender.userId !== 'local'
      ? {
          sender: {
            userId: item.sender.userId,
            name: item.sender.name,
            kind: 'user' as const,
            // Carry the OIDC picture so the queued bubble wears the SAME face
            // the landed row will — avatar parity across queued and landed.
            ...(item.sender.pictureUrl ? { pictureUrl: item.sender.pictureUrl } : {}),
          },
        }
      : {}),
  };
}

// ---- queued agent-to-agent messages (prime-agent) ---------------------------
//
// prime-agent queues an agent message that arrives while the lead is busy and
// shows it in its queue as a PREVIEW string (the fork's agent-session.ts
// queuedAgentMessagePreview): `Agent message received: <message>` — no
// sender — or, from a fork that names it, `Agent message from <from>: <message>`
// where <from> is `<relationship>:<name>`, a bare relationship word, or a bare
// name (the same grammar as the landed header). The queue carries text only,
// so the TEXT is the marker here: a person's queued message that itself
// starts with one of these labels would wear the agent chip in the transient
// strip (never in the transcript — the landed row is keyed on the custom's
// customType + details, never on text).

export const QUEUED_AGENT_MESSAGE_LABEL = 'Agent message received';
/** GRAMMAR SYNC with pi-stack/src/server/prime-driver.ts QUEUED_PLACEHOLDER. */
export const QUEUED_PLACEHOLDER = '(queued)';
const QUEUED_AGENT_FROM_RE = /^Agent message from ([^\n]{1,240}?): ([\s\S]*)$/;
const QUEUED_AGENT_ROLE_RE = /^(child|parent|sibling)(?::([\s\S]+))?$/;

/** A queue preview of an agent-to-agent message as the agent row it will
 * land as (agent sender, header-free body, the agent_message customType), or
 * null for any other queued text. The no-sender form carries
 * `queuedSenderUnknown` so the strip retires it on the landed text alone. */
export function queuedAgentMessageOf(text: string): PiMessage | null {
  const s = String(text ?? '');
  const anon = `${QUEUED_AGENT_MESSAGE_LABEL}: `;
  if (s.startsWith(anon)) {
    return {
      role: 'user',
      content: s.slice(anon.length).trim() ? s.slice(anon.length) : '',
      customType: AGENT_MESSAGE_CUSTOM_TYPE,
      sender: { userId: null, name: 'agent', kind: 'agent' },
      queuedSenderUnknown: true,
    } as PiMessage;
  }
  const m = QUEUED_AGENT_FROM_RE.exec(s);
  if (!m) return null;
  const from = (m[1] ?? '').trim();
  const rm = QUEUED_AGENT_ROLE_RE.exec(from);
  const role: AgentMessageRole = rm ? (rm[1] as AgentMessageRole) : 'peer';
  const named = rm ? (rm[2] ?? '').trim() : from;
  const name = named ? named.slice(0, 120) : role === 'peer' ? 'agent' : role;
  const body = m[2] ?? '';
  return {
    role: 'user',
    content: body.trim() ? body : '',
    customType: AGENT_MESSAGE_CUSTOM_TYPE,
    sender: { userId: null, name, kind: 'agent', role },
  } as PiMessage;
}

/** One pi queue_update ghost string → renderable user rows, through the SAME
 * ingest parse the landed echo goes through (normalizeUserEnvelope): sender
 * label lifted to a chip, injection markers recognized, trailing attachment
 * manifest folded into chips. Rows with nothing to show are dropped (an
 * attachment-only steer has no words until its blocks land). */
export function queuedGhostMessages(text: string): PiMessage[] {
  // The prime driver's stand-in for a text the wire did not send: a count,
  // never a message — rendering it drew a senderless "(queued)" USER bubble.
  if (text === QUEUED_PLACEHOLDER) return [];
  const a2a = queuedAgentMessageOf(text);
  if (a2a) return a2a.content ? [a2a] : [];
  return normalizeUserEnvelope({ role: 'user', content: String(text ?? '') }).filter((m) => {
    if (typeof m.content === 'string') return m.content.trim().length > 0;
    if (Array.isArray(m.content)) return m.content.length > 0;
    return false;
  });
}
